/*
 * As contagens "Novos (N)" e "Não lidos (N)" de quem entrou na conta.
 *
 * Chegam prontas no documento do fórum (canelaNewCount / canelaUnreadCount) e
 * são renovadas por GET /api/forums/canela-counts quando a pessoa volta à lista —
 * que é quando ela pode ter acabado de ler alguma coisa.
 *
 * Na página de uma tag, as abas mostram os números dela (`?tag=slug`): o "(1)" global numa categoria vazia mentia.
 */
import app from 'flarum/forum/app';
import { forumBool } from '../../utils/settings';
import { currentTag } from '../../utils/tags';
import { fetchTopicCounts } from './api';

/** O servidor para de contar aqui; a partir disso a aba mostra "99+". */
export const COUNT_LIMIT = 100;

/** Voltar à lista várias vezes seguidas não precisa refazer a contagem. */
const REFRESH_AFTER_MS = 15_000;
let state = null;
let refreshing = null;

/** As contagens por tag (slug), buscadas ao abrir a página de cada uma. */
const tagged = {};
const taggedRefreshing = {};
function currentTagSlug() {
  return currentTag()?.slug() || null;
}
function toCount(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : 0;
}
export function countsEnabled() {
  return !!app.session.user && forumBool('canelaStatsCountsEnabled', true);
}
function current() {
  if (!countsEnabled()) return null;
  if (!state) {
    const newCount = app.forum.attribute('canelaNewCount');
    const unreadCount = app.forum.attribute('canelaUnreadCount');

    // Sem os atributos (recurso desligado no servidor), não há o que mostrar.
    if (newCount === undefined && unreadCount === undefined) return null;
    state = {
      newCount: toCount(newCount),
      unreadCount: toCount(unreadCount),
      at: Date.now()
    };
  }
  return state;
}

/**
 * "Marcar tudo como lido" zera as duas na hora, sem esperar o servidor: a data
 * nova no usuário já diz que nada contado antes dela vale mais.
 */
function markedAllAfterCount(counts) {
  const markedAt = app.session.user?.markedAllAsReadAt();
  return !!markedAt && markedAt.getTime() > counts.at;
}

/**
 * As contagens que valem para `scope`: `'global'` (barra lateral) ou
 * `'page'` (as abas — a tag atual, quando a página é de uma tag).
 */
function scoped(scope) {
  const base = current();
  if (!base || scope === 'global') return base;
  const slug = currentTagSlug();
  if (!slug) return base;

  // Ainda sem o número da tag: busca e, enquanto isso, não mostra nada.
  if (!tagged[slug]) {
    refreshTag(slug);
    return null;
  }
  return tagged[slug];
}
export function newCount(scope = 'global') {
  const counts = scoped(scope);
  return counts && !markedAllAfterCount(counts) ? counts.newCount : 0;
}
export function unreadCount(scope = 'global') {
  const counts = scoped(scope);
  return counts && !markedAllAfterCount(counts) ? counts.unreadCount : 0;
}
function refreshTag(slug, force = false) {
  const counts = tagged[slug];
  if (!force && counts && Date.now() - counts.at < REFRESH_AFTER_MS && !markedAllAfterCount(counts)) return Promise.resolve();
  if (taggedRefreshing[slug]) return taggedRefreshing[slug];
  taggedRefreshing[slug] = fetchTopicCounts(slug).then(response => {
    if (!response) return;
    tagged[slug] = {
      newCount: toCount(response.new),
      unreadCount: toCount(response.unread),
      at: Date.now()
    };
    m.redraw();
  }).finally(() => {
    taggedRefreshing[slug] = undefined;
  });
  return taggedRefreshing[slug];
}

/** "3", ou "99+" quando o servidor parou de contar. */
export function formatCount(count) {
  return count >= COUNT_LIMIT ? `${COUNT_LIMIT - 1}+` : String(count);
}
export function refreshCounts(force = false) {
  const counts = current();
  if (!counts) return Promise.resolve();
  const slug = currentTagSlug();
  const tagRefresh = slug ? refreshTag(slug, force) : Promise.resolve();
  return Promise.all([refreshGlobal(counts, force), tagRefresh]).then(() => {});
}
function refreshGlobal(counts, force) {
  if (!force && Date.now() - counts.at < REFRESH_AFTER_MS && !markedAllAfterCount(counts)) return Promise.resolve();
  if (refreshing) return refreshing;
  refreshing = fetchTopicCounts().then(response => {
    if (!response) return;
    state = {
      newCount: toCount(response.new),
      unreadCount: toCount(response.unread),
      at: Date.now()
    };
    m.redraw();
  }).finally(() => {
    refreshing = null;
  });
  return refreshing;
}
flarum.reg.add('ramon-canela', 'forum/features/stats/counts', { COUNT_LIMIT: COUNT_LIMIT,countsEnabled: countsEnabled,newCount: newCount,unreadCount: unreadCount,formatCount: formatCount,refreshCounts: refreshCounts, });